import { createContext, useContext, type ReactNode } from 'react'

import { getFlagMetadata, type ClientFlagKey } from '@bettermode/flags/registry'

/**
 * The resolved values of the `client`-tagged flags for one request, keyed by flag
 * key. String-keyed rather than `Record<ClientFlagKey, boolean>` because it
 * arrives over the wire in `__BM_DATA__` — an old bundle can see a key the
 * current registry no longer has, and vice versa.
 */
export type FlagsSnapshot = Record<string, boolean>

/**
 * Frozen so the "no provider" default can't be mutated into a shared cache by a
 * consumer that treats the snapshot as writable.
 */
const EMPTY_SNAPSHOT: FlagsSnapshot = Object.freeze({})

const FlagsSnapshotContext = createContext<FlagsSnapshot>(EMPTY_SNAPSHOT)

interface FlagsSnapshotProviderProps {
  snapshot: FlagsSnapshot
  children: ReactNode
}

/**
 * Publishes the SSR flag snapshot to the React tree.
 *
 * This lives in `react-components`, not in `packages/web`, because it is the
 * lowest package both sides can import: `web` owns the snapshot (the server
 * resolves it and serializes it into the page) and mounts this provider through
 * its own `FlagsProvider`, while shared components in this package need to *read*
 * it. Nothing here talks to PostHog or OpenFeature — all evaluation stays
 * server-side.
 */
export const FlagsSnapshotProvider = ({
  snapshot,
  children,
}: FlagsSnapshotProviderProps) => (
  <FlagsSnapshotContext.Provider value={snapshot}>
    {children}
  </FlagsSnapshotContext.Provider>
)

/**
 * The whole snapshot, unkeyed. For tooling that reports what a page was rendered
 * with (the flag debug overlay); application code should read a single flag through
 * `useClientFlag` so the `ClientFlagKey` type keeps it honest.
 */
export const useFlagsSnapshot = (): FlagsSnapshot =>
  useContext(FlagsSnapshotContext)

/**
 * Read a `client`-tagged boolean flag from the SSR snapshot.
 *
 * With no `FlagsSnapshotProvider` above — Storybook, this package's test harness,
 * dev-portal — and for a key the server did not inject, this falls back to the
 * flag's registry-wide `defaultValue`, baked in at codegen from its `default-on` /
 * `default-off` tag in PostHog. So the fallback is never hand-coded at the call
 * site, and flipping the tag moves both the live value and the offline one.
 *
 * `key` is a `ClientFlagKey`, so only flags actually delivered to the browser can
 * be read here. The trade versus evaluating in the browser is that a flag change
 * lands on the next server-rendered response, not mid-session.
 */
export const useClientFlag = (key: ClientFlagKey): boolean => {
  const snapshot = useContext(FlagsSnapshotContext)
  // Via `getFlagMetadata`, not `FLAG_METADATA[key]`: an EMPTY registry makes
  // `FlagKey` `never`, and indexing `Record<never, FlagMetadata>` is a type error.
  // `?.` is belt-and-braces on top — a key without metadata should be impossible,
  // but a stale bundle pairing an old registry with new code shouldn't crash a render.
  return snapshot[key] ?? getFlagMetadata(key)?.defaultValue ?? false
}
